/**
 * LPQL Syntax Highlighter for documentation code blocks.
 * Mirrors the color scheme from apps/web QueryEditor.tsx.
 */

type TokenType =
  | 'plain'
  | 'pipe'
  | 'command'
  | 'function'
  | 'keyword'
  | 'parameter'
  | 'field'
  | 'string'
  | 'number'
  | 'operator'
  | 'paren'
  | 'comment'
  | 'whitespace';

interface Token {
  type: TokenType;
  value: string;
}

const PIPE_COMMANDS = new Set([
  'stats', 'timechart', 'chart', 'eval', 'table', 'fields', 'sort', 'head', 'tail',
  'top', 'rare', 'dedup', 'rex', 'where', 'rename', 'search',
  'fillnull', 'filldown', 'bin', 'bucket', 'join', 'makeresults',
]);

const STATS_FUNCTIONS = new Set([
  'count', 'c', 'avg', 'mean', 'sum', 'min', 'max',
  'dc', 'distinct_count', 'values', 'list',
  'first', 'last', 'latest', 'earliest',
  'median', 'mode', 'stdev', 'stdevp', 'var', 'variance', 'varp',
  'range', 'sumsq',
]);

const EVAL_FUNCTIONS = new Set([
  'if', 'case', 'coalesce', 'validate',
  'lower', 'upper', 'len', 'substr', 'trim', 'ltrim', 'rtrim', 'replace', 'split',
  'urlencode', 'urldecode', 'tostring', 'tonumber',
  'round', 'ceil', 'ceiling', 'floor', 'abs', 'pow', 'sqrt', 'log', 'ln', 'pi', 'exp',
  'isnull', 'isnotnull', 'isnum', 'isstr', 'typeof', 'null',
  'now', 'time', 'strftime', 'strptime', 'relative_time',
  'match', 'like', 'mvindex', 'mvcount', 'mvjoin', 'mvappend', 'mvdedup', 'mvsort', 'mvfilter', 'mvfind',
  'exact', 'searchmatch', 'cidrmatch', 'md5', 'sha1', 'sha256', 'sha512',
]);

const KEYWORDS = new Set(['by', 'as', 'over', 'in', 'and', 'or', 'not', 'true', 'false']);

const COMMAND_PARAMETERS: Record<string, Set<string>> = {
  timechart: new Set(['span', 'cont']),
  chart: new Set(['limit']),
  stats: new Set(['allnum', 'delim']),
  sort: new Set(['limit', 'desc', 'asc']),
  head: new Set(['limit']),
  tail: new Set(['limit']),
  top: new Set(['limit', 'countfield', 'percentfield', 'showcount', 'showperc']),
  rare: new Set(['limit', 'countfield', 'percentfield', 'showcount', 'showperc']),
  rex: new Set(['field', 'mode', 'max_match']),
  dedup: new Set(['sortby', 'keepevents', 'keepempty', 'consecutive']),
  fillnull: new Set(['value']),
  bin: new Set(['span', 'bins']),
  bucket: new Set(['span', 'bins']),
  join: new Set(['type']),
  makeresults: new Set(['count', 'annotate']),
};

const KNOWN_FIELDS = new Set([
  'source', 'host', 'level', 'event', 'timestamp', '_time', '_raw',
  'sourcetype', 'index', 'earliest', 'latest',
  'cluster', 'namespace', 'pod', 'container', 'node',
]);

// Search expression keywords that get highlighted before the first pipe
const SEARCH_KEYWORDS = new Set(['and', 'or', 'not', 'in', 'earliest', 'latest']);

function isAfterBy(tokens: Token[]): boolean {
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (tokens[i].type !== 'whitespace') {
      return tokens[i].type === 'keyword' && tokens[i].value.toLowerCase() === 'by';
    }
  }
  return false;
}

function tokenizeLine(input: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;

  const firstPipePos = input.indexOf('|');
  const beforePipe = firstPipePos === -1 ? input.length : firstPipePos;

  // Tokenize search expression (before first pipe) with basic highlighting
  while (pos < beforePipe) {
    const char = input[pos];

    if (/\s/.test(char)) {
      const start = pos;
      while (pos < beforePipe && /\s/.test(input[pos])) pos++;
      tokens.push({ type: 'whitespace', value: input.slice(start, pos) });
      continue;
    }

    if (char === '(' || char === ')') {
      tokens.push({ type: 'paren', value: char });
      pos++;
      continue;
    }

    if (char === ',') {
      tokens.push({ type: 'operator', value: char });
      pos++;
      continue;
    }

    if (char === '=' || char === '!' || char === '>' || char === '<') {
      const start = pos;
      if ((char === '!' || char === '>' || char === '<') && input[pos + 1] === '=') {
        pos += 2;
      } else if (char === '=' && input[pos + 1] === '=') {
        pos += 2;
      } else {
        pos++;
      }
      tokens.push({ type: 'operator', value: input.slice(start, pos) });
      continue;
    }

    if (char === '"' || char === "'") {
      const quote = char;
      const start = pos;
      pos++;
      while (pos < beforePipe && input[pos] !== quote) {
        if (input[pos] === '\\' && pos + 1 < beforePipe) pos += 2;
        else pos++;
      }
      if (pos < beforePipe) pos++;
      tokens.push({ type: 'string', value: input.slice(start, pos) });
      continue;
    }

    if (/[0-9]/.test(char) || (char === '-' && /[0-9]/.test(input[pos + 1] || ''))) {
      const start = pos;
      if (char === '-') pos++;
      while (pos < beforePipe && /[0-9.]/.test(input[pos])) pos++;
      if (pos < beforePipe && /[mhdwMy]/.test(input[pos])) pos++;
      tokens.push({ type: 'number', value: input.slice(start, pos) });
      continue;
    }

    if (char === '*') {
      tokens.push({ type: 'operator', value: char });
      pos++;
      continue;
    }

    if (/[a-zA-Z_]/.test(char)) {
      const start = pos;
      while (pos < beforePipe && /[a-zA-Z_0-9.*]/.test(input[pos])) pos++;
      const word = input.slice(start, pos);
      const wordLower = word.toLowerCase();
      const nextChar = input[pos];

      let tokenType: TokenType = 'plain';
      if (SEARCH_KEYWORDS.has(wordLower)) {
        tokenType = 'keyword';
      } else if (nextChar === '=' && KNOWN_FIELDS.has(wordLower)) {
        tokenType = 'field';
      } else if (nextChar === '=') {
        tokenType = 'field';
      } else if (KNOWN_FIELDS.has(wordLower)) {
        tokenType = 'field';
      }

      tokens.push({ type: tokenType, value: word });
      continue;
    }

    tokens.push({ type: 'plain', value: char });
    pos++;
  }

  // Tokenize pipe commands section
  let currentCommand: string | null = null;
  let inPipe = false;

  while (pos < input.length) {
    const char = input[pos];

    if (/\s/.test(char)) {
      const start = pos;
      while (pos < input.length && /\s/.test(input[pos])) pos++;
      tokens.push({ type: 'whitespace', value: input.slice(start, pos) });
      continue;
    }

    if (char === '|') {
      tokens.push({ type: 'pipe', value: char });
      pos++;
      currentCommand = null;
      inPipe = true;
      continue;
    }

    if (char === '(' || char === ')') {
      tokens.push({ type: 'paren', value: char });
      pos++;
      continue;
    }

    if (char === ',') {
      tokens.push({ type: 'operator', value: char });
      pos++;
      continue;
    }

    if (char === '=' || char === '!' || char === '>' || char === '<' ||
        char === '+' || char === '-' || char === '*' || char === '/' || char === '%' || char === '.') {
      const start = pos;
      if ((char === '!' || char === '>' || char === '<') && input[pos + 1] === '=') {
        pos += 2;
      } else if (char === '=' && input[pos + 1] === '=') {
        pos += 2;
      } else {
        pos++;
      }
      tokens.push({ type: 'operator', value: input.slice(start, pos) });
      continue;
    }

    if (char === '"' || char === "'") {
      const quote = char;
      const start = pos;
      pos++;
      while (pos < input.length && input[pos] !== quote) {
        if (input[pos] === '\\' && pos + 1 < input.length) pos += 2;
        else pos++;
      }
      if (pos < input.length) pos++;
      tokens.push({ type: 'string', value: input.slice(start, pos) });
      continue;
    }

    if (/[0-9]/.test(char) && (pos === 0 || /[\s(=,|+\-*/%><]/.test(input[pos - 1]))) {
      const start = pos;
      while (pos < input.length && /[0-9.]/.test(input[pos])) pos++;
      if (pos < input.length && /[mhdw]/.test(input[pos])) pos++;
      tokens.push({ type: 'number', value: input.slice(start, pos) });
      continue;
    }

    if (char === '-' && /[0-9]/.test(input[pos + 1] || '')) {
      const start = pos;
      pos++;
      while (pos < input.length && /[0-9]/.test(input[pos])) pos++;
      if (pos < input.length && /[mhdw]/.test(input[pos])) pos++;
      tokens.push({ type: 'number', value: input.slice(start, pos) });
      continue;
    }

    if (/[a-zA-Z_]/.test(char)) {
      const start = pos;
      while (pos < input.length && /[a-zA-Z_0-9]/.test(input[pos])) pos++;
      const word = input.slice(start, pos);
      const wordLower = word.toLowerCase();
      const nextChar = input[pos];

      let tokenType: TokenType = 'plain';

      if (inPipe) {
        if (currentCommand === null) {
          if (PIPE_COMMANDS.has(wordLower)) {
            tokenType = 'command';
          }
          currentCommand = wordLower;
        } else if (KEYWORDS.has(wordLower)) {
          tokenType = 'keyword';
        } else if (STATS_FUNCTIONS.has(wordLower) || EVAL_FUNCTIONS.has(wordLower) ||
                   /^(?:perc|p|exactperc|upperperc)\d+$/i.test(word)) {
          tokenType = 'function';
        } else if (currentCommand && COMMAND_PARAMETERS[currentCommand]?.has(wordLower) && nextChar === '=') {
          tokenType = 'parameter';
        } else if (nextChar === '=' || KNOWN_FIELDS.has(wordLower) || isAfterBy(tokens)) {
          tokenType = 'field';
        }
      }

      tokens.push({ type: tokenType, value: word });
      continue;
    }

    tokens.push({ type: 'plain', value: char });
    pos++;
  }

  return tokens;
}

const TOKEN_CLASSES: Record<TokenType, string> = {
  plain: 'text-gray-300',
  pipe: 'text-gray-500 font-bold',
  command: 'text-blue-400 font-semibold',
  function: 'text-purple-400 font-medium',
  keyword: 'text-orange-400 font-medium',
  parameter: 'text-green-400',
  field: 'text-cyan-400',
  string: 'text-yellow-300',
  number: 'text-emerald-300',
  operator: 'text-gray-400',
  paren: 'text-gray-400',
  comment: 'text-gray-600 italic',
  whitespace: '',
};

export function highlightLpql(code: string): React.ReactNode[] {
  const lines = code.split('\n');

  return lines.map((line, lineIdx) => {
    // Handle comment lines (starting with #)
    const trimmed = line.trimStart();
    if (trimmed.startsWith('#')) {
      const indent = line.slice(0, line.length - trimmed.length);
      return (
        <span key={lineIdx}>
          {lineIdx > 0 && '\n'}
          {indent}
          <span className={TOKEN_CLASSES.comment}>{trimmed}</span>
        </span>
      );
    }

    // Check for inline comments
    const commentIdx = line.indexOf('#');
    const queryPart = commentIdx >= 0 ? line.slice(0, commentIdx) : line;
    const commentPart = commentIdx >= 0 ? line.slice(commentIdx) : '';

    const tokens = tokenizeLine(queryPart);

    return (
      <span key={lineIdx}>
        {lineIdx > 0 && '\n'}
        {tokens.map((token, i) => (
          <span key={i} className={TOKEN_CLASSES[token.type]}>
            {token.value}
          </span>
        ))}
        {commentPart && <span className={TOKEN_CLASSES.comment}>{commentPart}</span>}
      </span>
    );
  });
}
